import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useMediaAssetCreateMutation, useMediaAssetDeleteMutation } from 'hooks/mutations/media/MediaAsset'
import { mediaPlatformAssetsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets'
import { mediaPlatformAssetRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets/Asset'
import { mediaAssetQueryKeys } from 'services/queryKeys/media'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { assetId } = mediaPlatformAssetRoute.useParams()
  const navigate = useNavigate({ from: mediaPlatformAssetRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const assetQuery = useQuery(mediaAssetQueryKeys.detail({ assetId }))
  const { data: asset } = assetQuery

  const { mutate: createAsset, ...assetCreateMutation } = useMediaAssetCreateMutation()
  const { mutate: deleteAsset } = useMediaAssetDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!asset) return

    setAnchorEl(undefined)

    deleteAsset(
      { assetId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:MediaPlatform.nameDelete', { name: asset.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: mediaPlatformAssetsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [asset, deleteAsset, assetId, enqueueSnackbar, t, navigate])

  const handleDuplication = useCallback(() => {
    if (!asset) return

    setAnchorEl(undefined)

    createAsset(
      { mediaAssetInsert: asset },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [asset, createAsset, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={asset?.name}
        entity={t('entity:publishing.asset', { count: 1 })}
      >
        <MenuItem onClick={handleDuplication}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {assetCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete staticLabel={t('delete')} onConfirm={handleDeletion} />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
